import {
  IBbox,
  IBboxType,
  ILabelType
} from 'annotation-tools/annotation-canvas';
import { SnackbarForPromise } from 'annotation-tools/ui-parts';
import {
  combineLatestTyped,
  IImage,
  IImageType,
  IList,
  IRect,
  IRectType,
  mergeTyped,
  PromiseSnackbarStateType,
  toPromiseSnackbarState,
  toRelativeRect,
  useDataStream,
  useEventAsStream,
  useStreamValue,
  useValueAsStream,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo } from 'react';
import { map, mapTo } from 'rxjs/operators';
import styled from 'styled-components';
import { imageId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { IAnnotationType } from '../../../types/record/annotation';
import { IImageInfo, IImageInfoType } from '../../../types/record/image-info';
import { snackbarMessages } from '../../rect-tool-with-crop/constants/messages/snackbar-message';
import { RectToolWithCropMainSub } from '../../rect-tool-with-crop/rect-tool-with-crop-main-sub';
import { useBboxList } from './hooks/use-bbox-list';
import { useCropRect } from './hooks/use-crop-rect';
import { useHandlers } from './hooks/use-handlers';

const Root = styled.div`
  height: 100%;
  width: 100%;
`;

interface Props {
  annotationList: IList<IAnnotationType>;
  imageList: IList<IImageInfoType>;
  labelList: IList<ILabelType>;
}

export const ModelImageRootSub: FC<Props> = memo((props: Readonly<Props>) => {
  /* prop stream */
  const annotationList$ = useValueAsStream(props.annotationList);
  const imageList$ = useValueAsStream(props.imageList);
  const labelList$ = useValueAsStream(props.labelList);

  /* events */

  const [closeSnackbarClick$, onCloseSnackbarClick] = useVoidEventAsStream();

  const [
    // dummy comment to control prettier
    postResultType$,
    setPostResultType
  ] = useEventAsStream<'success' | 'error'>();

  /* values */

  const imageIndex$ = useDataStream<number>(
    0,
    combineLatestTyped(imageList$, imageId$).pipe(
      map(([list, id]) => list.findIndex(e => e.id === id) ?? 0)
    )
  );

  const imageInfo$ = useDataStream<IImageInfoType>(
    IImageInfo(),
    combineLatestTyped(imageList$, imageId$).pipe(
      map(([list, id]) => list.find(e => e.id === id) ?? IImageInfo())
    )
  );

  const image$ = useDataStream<IImageType | undefined>(
    undefined,
    imageInfo$.pipe(
      map(imageInfo =>
        IImage({
          src: imageInfo.fileUri,
          rectSize: imageInfo.size
        })
      )
    )
  );

  const cropRectRelativeInput$ = useDataStream<IRectType | undefined>(
    undefined,
    imageInfo$.pipe(
      map(image => toRelativeRect(IRect(image.size))(image.cropRect))
    )
  );

  const {
    cropRectRelativeFromCanvas$,
    setCropRectRelativeFromCanvas,
    cropRectRelative
  } = useCropRect(cropRectRelativeInput$, imageInfo$, setPostResultType);

  const bboxRelativeListInput$ = useDataStream<IList<IBboxType> | undefined>(
    undefined,
    combineLatestTyped(
      annotationList$,
      labelList$,
      imageInfo$.pipe(map(image => toRelativeRect(IRect(image.size))))
    ).pipe(
      map(([annotationList, labelList, toRectRelativeToImageFn]) =>
        annotationList.map(an =>
          IBbox({
            id: an.uuid,
            rect: toRectRelativeToImageFn(an.rect),
            label: labelList.find(l => l.id === an.labelId)
          })
        )
      )
    )
  );

  const {
    bboxRelativeListFromCanvas$,
    setBboxRelativeListFromCanvas,
    bboxRelativeList$
  } = useBboxList(bboxRelativeListInput$, setPostResultType, imageInfo$);

  const isWaitingServerResponse$ = useDataStream<boolean>(
    false,
    mergeTyped(
      mergeTyped(bboxRelativeListFromCanvas$, cropRectRelativeFromCanvas$).pipe(
        mapTo(true)
      ),
      postResultType$.pipe(mapTo(false))
    )
  );

  const navButtonsIsDisabled$ = useDataStream<{
    firstPage: boolean;
    prevPage: boolean;
    nextPage: boolean;
    lastPage: boolean;
  }>(
    { firstPage: false, prevPage: false, nextPage: false, lastPage: false },
    isWaitingServerResponse$.pipe(
      map(b => ({
        firstPage: b,
        prevPage: b,
        nextPage: b,
        lastPage: b
      }))
    )
  );

  const snackbarState$ = useDataStream<PromiseSnackbarStateType>(
    {
      isOpen: false,
      message: '',
      promiseState: 'pending'
    },
    mergeTyped(
      mergeTyped(bboxRelativeListFromCanvas$, cropRectRelativeFromCanvas$).pipe(
        mapTo({
          isOpen: true,
          message: snackbarMessages.save.pending,
          promiseState: 'pending' as const
        })
      ),
      postResultType$.pipe(
        toPromiseSnackbarState(
          2000,
          resultType => ({
            isOpen: true,
            message:
              resultType === 'success'
                ? snackbarMessages.save.success
                : snackbarMessages.save.error,
            promiseState: resultType
          }),
          resultType => ({
            isOpen: false,
            message: '',
            promiseState: resultType
          })
        )
      ),
      closeSnackbarClick$.pipe(
        mapTo({
          isOpen: false,
          message: snackbarMessages.save.pending,
          promiseState: 'pending' as const
        })
      )
    )
  );

  /*  values for view */

  const image = useStreamValue(image$);
  const bboxRelativeList = useStreamValue(bboxRelativeList$);
  const snackbarState = useStreamValue(snackbarState$);
  const currentPageIndex = useStreamValue(imageIndex$, 1);
  const navButtonsIsDisabled = useStreamValue(navButtonsIsDisabled$);

  const { onPageIndexChange, onBackToListPageClick } = useHandlers(
    props.imageList
  );

  useLoadingPage(
    image === undefined ||
      cropRectRelative === undefined ||
      bboxRelativeList === undefined ||
      navButtonsIsDisabled === undefined
  );

  return (
    <Root>
      {image === undefined ||
      cropRectRelative === undefined ||
      bboxRelativeList === undefined ||
      navButtonsIsDisabled === undefined ? (
        undefined
      ) : (
        <RectToolWithCropMainSub
          image={image}
          cropRectRelative={cropRectRelative}
          bboxRelativeList={bboxRelativeList}
          labelList={props.labelList}
          navButtonsIsDisabled={navButtonsIsDisabled}
          currentPageIndex={currentPageIndex}
          pageLength={props.imageList.size}
          onPageIndexChange={onPageIndexChange}
          onBackToListPageClick={onBackToListPageClick}
          onBboxesRelativeChange={setBboxRelativeListFromCanvas}
          onCropRectRelativeChange={setCropRectRelativeFromCanvas}
          openSettingsDialogButtonState={'hide'}
        />
      )}
      <SnackbarForPromise
        isOpen={snackbarState?.isOpen ?? false}
        onCloseClick={onCloseSnackbarClick}
        message={snackbarState?.message ?? ''}
        promiseState={snackbarState?.promiseState}
      />
    </Root>
  );
});

ModelImageRootSub.displayName = 'ModelImageRootSub';
